export const FIELD_VERIFY_ERROR_GAP_PX = 6
export const FIELD_VERIFY_ERROR_INSET_PX = 8

export type FieldVerifyErrorPlacement = 'above' | 'below'

export type FieldVerifyErrorAnchor = {
  top: number
  left: number
  width: number
  height: number
  bottom: number
}

export type FieldVerifyErrorBox = {
  top: number
  left: number
  maxWidth: number
  placement: FieldVerifyErrorPlacement
}

export type FieldVerifyErrorVisualScale = {
  originVisual: { top: number; left: number }
  scaleX: number
  scaleY: number
}

/** Visual viewport point -> CSS top/left for position:fixed. scale is visual px per 1 CSS px. */
export function cssFixedFromVisualPoint(
  target: { top: number; left: number },
  sample: FieldVerifyErrorVisualScale,
): { top: number; left: number } {
  const scaleX = Number.isFinite(sample.scaleX) && sample.scaleX !== 0 ? sample.scaleX : 1
  const scaleY = Number.isFinite(sample.scaleY) && sample.scaleY !== 0 ? sample.scaleY : 1
  return {
    top: (target.top - sample.originVisual.top) / scaleY,
    left: (target.left - sample.originVisual.left) / scaleX,
  }
}

/** Prefer under the field; flip above only when the message would leave the viewport. */
export function placeFieldVerifyErrorOverlay(args: {
  anchor: FieldVerifyErrorAnchor
  balloonHeight: number
  viewport: { width: number; height: number }
  gapPx?: number
  insetPx?: number
}): FieldVerifyErrorBox {
  const gap = args.gapPx ?? FIELD_VERIFY_ERROR_GAP_PX
  const inset = args.insetPx ?? FIELD_VERIFY_ERROR_INSET_PX
  const vw = args.viewport.width
  const vh = args.viewport.height
  const balloonHeight = Math.max(0, args.balloonHeight)
  const needed = balloonHeight + gap
  const spaceAbove = args.anchor.top - inset
  const spaceBelow = vh - args.anchor.bottom - inset
  let placement: FieldVerifyErrorPlacement
  if (needed <= spaceBelow) placement = 'below'
  else if (needed <= spaceAbove) placement = 'above'
  else placement = spaceBelow >= spaceAbove ? 'below' : 'above'

  const maxWidth = Math.max(0, Math.min(args.anchor.width, Math.max(0, vw - inset * 2)))
  const maxLeft = Math.max(inset, vw - inset - maxWidth)
  const left = Math.min(Math.max(args.anchor.left, inset), maxLeft)

  const rawTop =
    placement === 'above'
      ? args.anchor.top - gap - balloonHeight
      : args.anchor.bottom + gap
  const maxTop = Math.max(inset, vh - inset - balloonHeight)
  const top = Math.min(Math.max(rawTop, inset), maxTop)

  return { top, left, maxWidth, placement }
}
